'use client'

import { useState, useRef, useMemo, useCallback } from 'react'

import { faGavel, faSlidersH } from '@fortawesome/pro-solid-svg-icons'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { FormattedMessage, Pagination } from '@b-stock/fe-core/bstock-react'
import type { StoreFrontDto } from '@b-stock/listing-api-client'

import ProductStructuredData from '@/components/tracking/ProductStructuredData'
import { FeatureFlags } from '@/helpers/featureFlags'
import { PermissionGranule, usePermission } from '@/helpers/permissions'
import { ItemListProvider } from '@/helpers/telemetry/itemListContext'
import { ScreenName } from '@/helpers/telemetry/ScreenName'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { useViewItemList } from '@/helpers/telemetry/useViewItemList'
import newSellerStorefrontsQuery from '@/queries/newSellerStorefrontsQuery'
import { allWatchedListingsQuery } from '@/queries/watchlistQuery'

import { CompareEligibilityBanner } from '../compare/CompareEligibilityBanner'
import { ComparePanel } from '../compare/ComparePanel'
import { CompareToggleButton } from '../compare/CompareToggleButton'
import { CompareTray, CompareTraySpacer } from '../compare/CompareTray'
import { MyBidsFilterToggle } from '../ListingSearchView/MyBidsFilterToggle'
import { ViewToggle } from '../ListingSearchView/ViewToggle'
import { AppliedFilters } from '../search/AppliedFilters'
import { useListingSearch } from '../search/ListingSearchProvider'
import PerPageDropdown from '../search/perPageDropdown/PerPageDropdown'
import SavedSearchButton from '../search/savedSearch/SavedSearchButton'
import { useAllListingsFilters } from '../search/SearchFilters'
import SortByDropdown from '../search/sortByDropdown/SortByDropdown'
import StorefrontListingSearch from '../search/StorefrontListingSearch'
import { useClearBulkSelectionOnFilterChange } from '../shared/BulkActionsBar'
import FiltersTopBar from '../shared/FiltersTopBar'
import {
  FiltersBarContainer,
  ListingsInnerContainer,
  TotalListings,
  SortContainer,
  FiltersButton,
  FiltersIcon,
  ResultsRow,
  SearchSortControls,
  PaginationWrapper,
  ListingsOuterContainer,
} from '../shared/listings-ui'
import { useIsMobileTableLayout } from '../shared/useIsMobileTableLayout'
import { useThresholdEmptyState } from '../shared/useThresholdEmptyState'
import SimilarInventory from '../SimilarInventory'
import FiltersBar from './FiltersBar'
import ListingGrid, { type ListingGridEmptyState } from './ListingGrid'
import { useGridNotesById } from './useGridNotesById'

const baseUrl =
  typeof window !== 'undefined' ? window.location.origin : 'https://bstock.com'

function ListingGridWithFilters({
  emptyState,
  excludeSavedSearch,
  resultsTitle,
  storefront,
}: {
  /** Replaces the generic "no results" state; My Bids and threshold still win. */
  emptyState?: ListingGridEmptyState
  excludeSavedSearch?: boolean
  /** Replaces the "N Results" heading above the grid. */
  resultsTitle?: React.ReactNode
  storefront?: StoreFrontDto
}) {
  const searchCtx = useListingSearch()

  const {
    data,
    error,
    isLoading,
    myBidsFilter,
    setPage,
    state,
    updateFilters,
  } = searchCtx
  const isMyBidsLoading = !!state.filters.myBids && myBidsFilter.isLoading
  const thresholdEmptyState = useThresholdEmptyState()
  const listings = useMemo(() => data?.listings ?? [], [data?.listings])

  const { filters, hiddenFilters } = useAllListingsFilters(storefront)
  const isMobileFiltersShown = useIsMobileTableLayout()
  const { trackButtonClicked } = useAnalytics()
  const isListViewBetaEnabled = useFlag(
    FeatureFlags.ENABLE_BETA_AUCTION_LIST_VIEW
  )

  const isWatchlistHeartEnabled = useFlag(
    FeatureFlags.ENABLE_WATCHLIST_HEART_ON_CARDS
  )
  const isNewSellerTreatment =
    useFlag(FeatureFlags.NEW_SELLER_ALP_LABEL) === 'treatment'
  const { data: newSellerIds, isError: isNewSellerIdsError } =
    newSellerStorefrontsQuery.useWithOpts([], {
      enabled: isNewSellerTreatment,
    })
  const personId = useDecodedAccessToken()?.personId

  const { data: watchlistEntries } = allWatchedListingsQuery.useWithOpts(
    [personId ?? ''],
    { enabled: isWatchlistHeartEnabled && !!personId }
  )

  const watchedIds = useMemo(
    () => new Set((watchlistEntries ?? []).map((e) => e.listingId)),
    [watchlistEntries]
  )

  const notesById = useGridNotesById({
    personId,
    listings,
    watchlistEntries,
    isWatchlistHeartEnabled,
  })

  // Set watchlistStatus (3MP only), which enables the watchlist heart on the card
  const enrichedListings = useMemo(() => {
    if (!isWatchlistHeartEnabled) return listings

    return listings.map((listing) => {
      if (listing.siteAbb.toLowerCase() !== 'tmp') return listing

      const isWatchlisted = watchedIds.has(listing.listingId)
      const note = notesById[listing.listingId]
      return {
        ...listing,
        watchlistStatus: {
          isWatchlisted,
          ...(note ? { note } : {}),
        },
      }
    })
  }, [isWatchlistHeartEnabled, listings, watchedIds, notesById])

  // While the myBids id fetch is in flight, don't render the stale
  // (unfiltered) cards underneath the loading state — clear them so the
  // grid's loading skeleton shows immediately instead of the old list
  // lingering until the new results arrive.
  const visibleListings = isMyBidsLoading ? [] : enrichedListings

  const itemListId = storefront
    ? `seller-storefront-${storefront._id}`
    : 'all-auctions-grid'

  const itemListName = storefront
    ? `Seller Storefront: ${storefront.name}`
    : 'All Auctions - Grid View'

  const isNewSeller = useCallback(
    (listing: { storefrontId: string }) =>
      isNewSellerTreatment &&
      (newSellerIds?.has(listing.storefrontId) ?? false),
    [isNewSellerTreatment, newSellerIds]
  )

  useViewItemList({
    listings,
    itemListId,
    itemListName,
    enabled:
      !isLoading &&
      !error &&
      listings.length > 0 &&
      (!isNewSellerTreatment ||
        newSellerIds !== undefined ||
        isNewSellerIdsError),
    isNewSeller,
  })

  const [showMobileFilterModal, setShowMobileFilterModal] = useState(false)
  const openMobileFilterModal = () => setShowMobileFilterModal(true)
  const closeMobileFilterModal = () => setShowMobileFilterModal(false)
  const listingsRef = useRef<HTMLDivElement>(null)

  // NOTE: intentionally does NOT subscribe to compare selection here — doing so
  // would re-render the entire grid on every add/remove. The tray, spacer and
  // panel subscribe on their own as leaf nodes instead; panel open state lives
  // in CompareSelectionProvider, so opening it doesn't re-render the grid.

  const canReadSavedSearches = usePermission(
    PermissionGranule.FEBP_SAVED_SEARCHES_READ
  )

  // Only show relevance sort option if there is an active search query
  const showRelevance = !!state.filters.q

  useClearBulkSelectionOnFilterChange(state.filters)

  const handlePageChange = (page: number) => {
    // Scroll to top of listings area
    listingsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })

    trackButtonClicked({ buttonName: 'pagination', page })
    setPage(page)
  }

  return (
    <>
      <ProductStructuredData baseUrl={baseUrl} listings={listings} />
      <ListingsOuterContainer ref={listingsRef}>
        <FiltersBarContainer>
          <FiltersBar
            showFilterModal={showMobileFilterModal}
            closeFilterModal={closeMobileFilterModal}
            searchCtx={searchCtx}
            showRelevance={showRelevance}
            filters={filters}
            hiddenFilters={hiddenFilters}
            storefront={storefront}
            excludeSavedSearch={excludeSavedSearch}
          />
        </FiltersBarContainer>
        <ListingsInnerContainer>
          <FiltersTopBar>
            <AppliedFilters
              filters={filters}
              searchCtx={searchCtx}
              excludeSavedSearch={excludeSavedSearch}
            />

            <ResultsRow>
              <TotalListings data-testid="ResultsCount">
                {resultsTitle ?? (
                  <FormattedMessage
                    id="Listings.results"
                    values={{ count: isLoading ? '' : data?.total || 0 }}
                  />
                )}
              </TotalListings>

              <SearchSortControls>
                <MyBidsFilterToggle />
                {!isMobileFiltersShown && (
                  <>
                    {storefront ? (
                      // On Seller Storefront, the search bar is on the page
                      // On All Listings, the search bar in the header is used
                      <StorefrontListingSearch storefront={storefront} />
                    ) : null}
                    <PerPageDropdown />
                    <SortContainer>
                      <SortByDropdown showRelevance={showRelevance} />
                    </SortContainer>
                  </>
                )}
                {isMobileFiltersShown && (
                  <>
                    {!excludeSavedSearch && canReadSavedSearches && (
                      <SavedSearchButton />
                    )}
                    <FiltersButton
                      appearance="secondary"
                      onClick={openMobileFilterModal}
                    >
                      <FiltersIcon icon={faSlidersH} />
                      <span>
                        <FormattedMessage id="Filters.Mobile.FiltersButton.title" />
                      </span>
                    </FiltersButton>
                  </>
                )}
                <CompareToggleButton />
                {isListViewBetaEnabled && <ViewToggle />}
              </SearchSortControls>
            </ResultsRow>
          </FiltersTopBar>

          <ItemListProvider itemListId={itemListId} itemListName={itemListName}>
            <ListingGrid
              emptyState={
                state.filters.myBids
                  ? {
                      icon: faGavel,
                      title: (
                        <FormattedMessage id="Listings.myBidsFilter.empty.title" />
                      ),
                      description: (
                        <FormattedMessage id="Listings.myBidsFilter.empty.description" />
                      ),
                      primaryButton: {
                        label: (
                          <FormattedMessage id="Listings.myBidsFilter.empty.cta" />
                        ),
                        handleClick: () => updateFilters({ myBids: false }),
                      },
                    }
                  : (thresholdEmptyState ?? emptyState)
              }
              error={error}
              isLoading={isLoading || isMyBidsLoading}
              listings={visibleListings}
              noListings={data?.total === 0 && !isMyBidsLoading}
              screenName={
                storefront ? ScreenName.SellerStorefront : ScreenName.AllAuction
              }
            />
          </ItemListProvider>

          {!!data?.total && (
            <PaginationWrapper>
              <Pagination
                page={state.page}
                perPage={data.limit}
                setPage={handlePageChange}
                total={data.total}
              />
            </PaginationWrapper>
          )}
          <SimilarInventory storefront={storefront} scrollRef={listingsRef} />
        </ListingsInnerContainer>
      </ListingsOuterContainer>

      <CompareTraySpacer />
      <CompareTray />
      <ComparePanel banner={<CompareEligibilityBanner />} />
    </>
  )
}

export default ListingGridWithFilters
